import React, { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react';
import { authApi, branchesApi, branchStorage, tokenStorage, rbacApi } from '@/lib/api';
import { DEFAULT_BRANCH, getAvailableBranches, isAdminRole, isSuperAdminRole, normalizeBranchName } from '@/lib/branches';
import type { AuthUser, Branch, LoginPayload } from '@/types';

interface AuthContextValue {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  selectedBranch: string;
  availableBranches: string[];
  branchList: Branch[];
  isAdmin: boolean;
  isSuperAdmin: boolean;
  permissions: string[];
  hasPermission: (permission: string) => boolean;
  setSelectedBranch: (branch: string) => void;
  login: (payload: LoginPayload) => Promise<void>;
  logout: () => void;
  loadCurrentUser: () => Promise<void>;
}

const AuthCtx = createContext<AuthContextValue>({
  user: null,
  isAuthenticated: false,
  isLoading: false,
  selectedBranch: '',
  availableBranches: [],
  branchList: [],
  isAdmin: false,
  isSuperAdmin: false,
  permissions: [],
  hasPermission: () => false,
  setSelectedBranch: () => undefined,
  login: async () => undefined,
  logout: () => undefined,
  loadCurrentUser: async () => undefined,
});

function synthesizeBranches(slugs: string[]): Branch[] {
  return slugs.map((slug, idx) => ({
    id: -(idx + 1),
    slug,
    display_name: slug,
    is_active: true,
  } as Branch));
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [permissions, setPermissions] = useState<string[]>([]);
  const [selectedBranch, setSelectedBranchState] = useState(() => branchStorage.getSelected());
  const [branchList, setBranchList] = useState<Branch[]>([]);

  const isAdmin = useMemo(() => isAdminRole(user?.role), [user?.role]);
  const isSuperAdmin = useMemo(() => isSuperAdminRole(user?.role), [user?.role]);

  // Slugs the user may access based on their own profile payload. Used to
  // filter branchList for non-super-admins and as a fallback when the
  // branches API is unreachable.
  const userAllowedSlugs = useMemo(() => getAvailableBranches(user), [user]);

  // Fetch the full branch directory whenever the authenticated user changes.
  useEffect(() => {
    if (!user) {
      setBranchList([]);
      return;
    }
    let cancelled = false;
    branchesApi
      .list({ is_active: true, limit: 100 })
      .then((res) => {
        if (cancelled) return;
        const active = (res.data ?? []).filter((b) => b.is_active);
        const allowed = new Set(userAllowedSlugs.map((s) => s.toLowerCase()));
        const filtered = isSuperAdmin
          ? active
          : active.filter((b) => allowed.has((b.slug || '').toLowerCase()));
        setBranchList(filtered.length > 0 ? filtered : synthesizeBranches(userAllowedSlugs));
      })
      .catch(() => {
        if (cancelled) return;
        setBranchList(synthesizeBranches(userAllowedSlugs));
      });
    return () => { cancelled = true; };
  }, [user, isSuperAdmin, userAllowedSlugs]);

  // availableBranches (slug list) — derived from branchList so the TopBar
  // dropdown and any slug-based consumer agree on the same set of branches.
  const availableBranches = useMemo(() => {
    if (branchList.length > 0) return branchList.map((b) => b.slug);
    return userAllowedSlugs;
  }, [branchList, userAllowedSlugs]);

  useEffect(() => {
    if (!user) {
      setSelectedBranchState('');
      branchStorage.clearSelected();
      return;
    }
    // Wait for branchList to populate before resolving the stored selection —
    // otherwise a super admin's cross-branch selection could be overwritten
    // while the directory fetch is still in flight.
    if (branchList.length === 0) return;

    const storedBranch = normalizeBranchName(branchStorage.getSelected());
    const storedMatch = availableBranches.find((branch) => branch.toLowerCase() === storedBranch.toLowerCase());
    const nextBranch = isSuperAdmin
      ? (storedMatch ?? (normalizeBranchName(user.branch) || availableBranches[0] || DEFAULT_BRANCH))
      : (normalizeBranchName(user.branch) || availableBranches[0] || DEFAULT_BRANCH);

    setSelectedBranchState((current) => (current === nextBranch ? current : nextBranch));
    branchStorage.setSelected(nextBranch);
  }, [availableBranches, isSuperAdmin, user, branchList.length]);

  const setSelectedBranch = useCallback((branch: string) => {
    const normalizedBranch = normalizeBranchName(branch);
    const homeBranch = normalizeBranchName(user?.branch) || availableBranches[0] || DEFAULT_BRANCH;

    if (!normalizedBranch) return;

    if (!isSuperAdmin) {
      setSelectedBranchState(homeBranch);
      branchStorage.setSelected(homeBranch);
      return;
    }

    const matchingBranch = availableBranches.find((item) => item.toLowerCase() === normalizedBranch.toLowerCase()) ?? normalizedBranch;
    setSelectedBranchState(matchingBranch);
    branchStorage.setSelected(matchingBranch);
  }, [availableBranches, isSuperAdmin, user?.branch]);

  const fetchPermissions = useCallback(async () => {
    try {
      const res = await rbacApi.getMyPermissions();
      setPermissions(res.permissions);
    } catch {
      setPermissions([]);
    }
  }, []);

  const hasPermission = useCallback(
    (permission: string): boolean => {
      if (isSuperAdmin) return true;
      if (permissions.includes('*')) return true;
      return permissions.includes(permission);
    },
    [isSuperAdmin, permissions],
  );

  const login = useCallback(async (payload: LoginPayload) => {
    setIsLoading(true);
    try {
      const res = await authApi.login(payload);
      tokenStorage.setTokens(res.accessToken, res.refreshToken);
      setUser(res.user);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Fetch permissions whenever user changes (login or page reload)
  useEffect(() => {
    if (user) {
      fetchPermissions();
    } else {
      setPermissions([]);
    }
  }, [user, fetchPermissions]);

  const logout = useCallback(() => {
    tokenStorage.clearTokens();
    branchStorage.clearSelected();
    setSelectedBranchState('');
    setPermissions([]);
    setUser(null);
  }, []);

  const loadCurrentUser = useCallback(async () => {
    const token = tokenStorage.getAccess();
    if (!token) return;
    setIsLoading(true);
    try {
      const me = await authApi.me();
      setUser(me);
    } catch {
      tokenStorage.clearTokens();
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isAuthenticated: !!user,
      isLoading,
      selectedBranch,
      availableBranches,
      branchList,
      isAdmin,
      isSuperAdmin,
      permissions,
      hasPermission,
      setSelectedBranch,
      login,
      logout,
      loadCurrentUser,
    }),
    [user, isLoading, selectedBranch, availableBranches, branchList, isAdmin, isSuperAdmin, permissions, hasPermission, setSelectedBranch, login, logout, loadCurrentUser],
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export function useAuth(): AuthContextValue {
  return useContext(AuthCtx);
}

